/**
 * Deterministic, seedable pseudo-random number generation.
 *
 * Palimpsest must be reproducible: the same seed produces the same city, and the
 * evolution engine must be able to make "random" choices that are stable across
 * save/load. We therefore never use Math.random() anywhere in generation — all
 * randomness flows through a seeded RNG instance.
 *
 * mulberry32 is a tiny, fast, statistically decent 32-bit generator. It is more
 * than sufficient for procedural world building and avoids pulling in a heavy
 * dependency.
 */

export class RNG {
  private state: number;
  readonly seed: number;

  constructor(seed = 1) {
    // Coerce to a non-zero unsigned 32-bit integer.
    this.seed = seed >>> 0 || 0x9e3779b9;
    this.state = this.seed;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** True with probability p. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** Pick a uniformly random element from an array. */
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }

  /** Weighted pick. weights need not sum to 1. */
  weighted<T>(items: readonly T[], weights: readonly number[]): T {
    let total = 0;
    for (const w of weights) total += w;
    let r = this.next() * total;
    for (let i = 0; i < items.length; i++) {
      r -= weights[i];
      if (r <= 0) return items[i];
    }
    return items[items.length - 1];
  }

  /** In-place Fisher–Yates shuffle. */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  /** Derive a new independent RNG from this one (for sub-systems). */
  fork(salt = 0): RNG {
    return new RNG((this.state ^ (salt * 0x85ebca6b)) >>> 0);
  }

  /** Snapshot / restore for deterministic save systems. */
  getState(): number {
    return this.state;
  }
  setState(state: number): void {
    this.state = state >>> 0;
  }
}

/** Convert an arbitrary string into a 32-bit seed (xfnv1a). */
export function hashSeed(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
